import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getComments, createComment, updateComment, deleteComment, likeComment, unlikeComment } from '../api/comments';
import type { Comment } from '../types/mjai';
import type { CommentPayload } from '../api/comments';

export function useComments(handId: string | undefined) {
  return useQuery<Comment[]>({
    queryKey: ['comments', handId],
    queryFn: () => getComments(handId!),
    enabled: !!handId,
  });
}

export function useCreateComment(handId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CommentPayload) => createComment(handId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['comments', handId] }),
  });
}

export function useUpdateComment(handId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, content }: { commentId: string; content: string }) =>
      updateComment(commentId, content),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['comments', handId] }),
  });
}

export function useDeleteComment(handId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => deleteComment(commentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['comments', handId] }),
  });
}

export function useLikeComment(handId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, isLiked }: { commentId: string; isLiked: boolean }) =>
      isLiked ? unlikeComment(commentId) : likeComment(commentId),
    onSuccess: (data, { commentId }) => {
      queryClient.setQueryData<Comment[]>(['comments', handId], (old) =>
        old?.map((c) =>
          c.id === commentId
            ? { ...c, like_count: data.like_count, is_liked: data.is_liked }
            : c
        ) ?? []
      );
    },
  });
}
